import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf, faClock, faTriangleExclamation, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => (
    <div
        style={{
            flex: '1 1 180px', minWidth: 170, maxWidth: 260,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 4px 16px ${border}bb`)}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
);

const SectionBlock = ({ title, accent, cards }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: accent }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
    </div>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');

const PendingSummaryCards = ({ summary }) => {
    const { t } = useTranslation();
    if (!summary) return null;
    const b = summary.buckets ?? {};

    const overviewCards = [
        {
            label: t('assignedToChallan.cards.totalPending'),
            value: fmt(summary.total_pending),
            sub: t('assignedToChallan.cards.awaitingChallan'),
            icon: faHourglassHalf,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        {
            label: t('assignedToChallan.cards.over1Day'),
            value: fmt(summary.over_1_day),
            sub: t('assignedToChallan.cards.waiting24h'),
            icon: faClock,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
        {
            label: t('assignedToChallan.cards.over7Days'),
            value: fmt(summary.over_7_days),
            sub: t('assignedToChallan.cards.criticallyStuck'),
            icon: faTriangleExclamation,
            color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
        },
    ];

    const bucketCards = [
        { label: t('assignedToChallan.buckets.today'), value: fmt(b.today), icon: faLayerGroup, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
        { label: t('assignedToChallan.buckets.oneToTwoDays'), value: fmt(b['1d']), icon: faLayerGroup, color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
        { label: t('assignedToChallan.buckets.twoToSevenDays'), value: fmt(b['2-7d']), icon: faLayerGroup, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
        { label: t('assignedToChallan.buckets.overSevenDays'), value: fmt(b.over_7d), icon: faLayerGroup, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
    ];

    const hasBuckets = Object.keys(b).length > 0;

    return (
        <div>
            <SectionBlock title={t('assignedToChallan.sections.backlogOverview')} accent="#189AB4" cards={overviewCards} />
            {hasBuckets && <SectionBlock title={t('assignedToChallan.sections.agingBuckets')} accent="#7c3aed" cards={bucketCards} />}
        </div>
    );
};

export default PendingSummaryCards;
